require('./globalPositionExpenses.scss');

const PropTypes = require('prop-types');

// @vendors
const React = require('react');
const PureRenderMixin = require('react-addons-pure-render-mixin');
const classnames = require('classnames');

// @utilities
const moment = require('moment');
const { FormattedText, formatText } = require('core/i18n').i18n;
const { formatAmount, formatCurrency } = require('utilities/currencyHelper');
const { toFirstUppercase, toCamelCase } = require('utilities/stringHelper');

// @components
const GlobalPositionExpensesGraph = require('./globalPositionExpensesGraph/globalPositionExpensesGraph');
const GlobalPositionExpensesFilterSettings = require('./globalPositionExpensesFilterSettings/globalPositionExpensesFilterSettings');
const GlobalPositionExpensesEmptyState = require('./globalPositionExpensesEmptyState/globalPositionExpensesEmptyState');

// @commons
const { OKButton } = require('components/commons/OKButton/OKButton');
const OKTooltip = require('components/commons/OKTooltip/OKTooltip');
const { LoadingSection } = require('commonsComponents/loadingSection');

// @constants
const {
    GP_EXPENSES_FILTER_TYPE_ALL,
    GP_EXPENSES_FILTER_COMPARE_NONE,
    EURO_TEXT
} = require('constants/index');
const { DATE_PICKER_CUSTOM_LOCALE } = require('constants/commonsComponents');

// @images
const openTutorialImg = require('assets/images/icon-tutorial.svg');

class GlobalPositionExpenses extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            filterSettingsOpen: false
        };

        this.handleClickOnFilterSettings = this.handleClickOnFilterSettings.bind(this);
        this.handleFilterSettingsClose = this.handleFilterSettingsClose.bind(this);
        this.shouldComponentUpdate = PureRenderMixin.shouldComponentUpdate.bind(this);
    }

    componentDidMount() {
        this.props.actions.requestExpenses();
    }

    componentWillReceiveProps() {
        // TODO: check if this method is called ONLY if filters have changed
        // If it does, we should remove 'this.props.onCloseClick();' on handleApplyClick of globalPositionExpensesFilterSetings.js
        if (this.state.filterSettingsOpen) {
            this.setState({
                filterSettingsOpen: false
            });
        }
    }

    buildCurrentPeriodBullet(isFilterComparing) {
        let currentPeriodBullet;
        if (isFilterComparing) {
            currentPeriodBullet = (
                <span className="global-position-expenses__bullet">
                    {' '}
                </span>
            );
        }
        return currentPeriodBullet;
    }

    buildPreviousPeriodTotal(isFilterComparing, totalComparePeriod, currency) {
        const previousMonth = toFirstUppercase(moment().subtract(1, 'months').locale(DATE_PICKER_CUSTOM_LOCALE).format("MMMM"));
        let previousPeriodComponent;
        if (isFilterComparing) {
            previousPeriodComponent = (
                <p className="global-position-expenses__expended-previous-amount">
                    <span className="global-position-expenses__bullet global-position-expenses__bullet--previous">
                        {' '}
                    </span>
                    <span className="global-position-expenses__month-text">
                        {previousMonth}
                    </span>
                    <span className="global-position-expenses__previous-expended-amount">
                        {formatAmount(totalComparePeriod)}{' '}
                        {formatCurrency(currency)}
                    </span>
                </p>
            );
        }

        return previousPeriodComponent;
    }

    handleClickOnFilterSettings() {
        this.setState({ filterSettingsOpen: true });
    }

    handleFilterSettingsClose() {
        this.setState({ filterSettingsOpen: false });
    }

    hasDefaultFilters(filters) {
        return filters.get('type') === GP_EXPENSES_FILTER_TYPE_ALL;
    }

    render() {
        const { immExpenses } = this.props;
        const filters = immExpenses.get('filters');
        const immBaseData = immExpenses.get('expenses');
        const filterType = filters.get('type');
        const totalCurrentPeriod = immBaseData.getIn(['currentPeriod', 'total', filterType]);
        const formattedTotalCurrentPeriod = formatAmount(totalCurrentPeriod);


        const totalComparePeriod = immBaseData.getIn(['previousPeriod', 'total', filterType]);
        const currency = EURO_TEXT; // The componente only supports EUROS for now
        const currentMonth = toFirstUppercase(
            moment().locale(DATE_PICKER_CUSTOM_LOCALE).format('MMMM')
        );

        const isFiltersApplied = !this.hasDefaultFilters(filters);
        const isFilterComparing = filters.get('compare') !== GP_EXPENSES_FILTER_COMPARE_NONE;
        const hasExpenses = totalCurrentPeriod || (isFilterComparing && totalComparePeriod);

        let noExpensesMessage = 'myGlobalPositionExpenses-defaultNoExpensesMessage';
        if (isFiltersApplied) {
            noExpensesMessage = 'myGlobalPositionExpenses-noExpensesMessageWithFilter';
        } else if (isFilterComparing) {
            noExpensesMessage = 'myGlobalPositionExpenses-noExpensesMessageWithCompare';
        }

        let content, loading;
        if (this.state.filterSettingsOpen) {
            content = (
                <GlobalPositionExpensesFilterSettings
                    filters={filters}
                    updateFilters={this.props.actions.updateFilters}
                    requestFilteredExpenses={this.props.actions.requestExpenses}
                    onCloseClick={this.handleFilterSettingsClose}
                />
            );

        } else if (immExpenses.get('isFetching')) {
            loading = true;
            content = (
                <div className="global-position-expenses__loader">
                    <LoadingSection />
                </div>
            );
        } else {
            const filterButton = (
                <div className="global-position-expenses__filter-settings-button">
                    <OKButton
                        modifier="task"
                        inverse={true}
                        onClick={this.handleClickOnFilterSettings}
                    >
                        <i className="icon-filtrar" />
                    </OKButton>
                </div>
            );
            const filterButtonTooltipText = toCamelCase(
                formatText('myGlobalPositionExpenses-filterButtonTooltipText')
            );
            const previousPeriodComponent = this.buildPreviousPeriodTotal(
                isFilterComparing,
                totalComparePeriod,
                currency
            );
            const currentPeriodBullet = this.buildCurrentPeriodBullet(isFilterComparing);
            const tutorialTooltipTarget = (
                <img
                    src={openTutorialImg}
                    className="global-position-expenses__tooltip-element"
                />
            );

            // If filters are not visible (main content is visible)
            let mainSection;
            if (hasExpenses) {
                // Expenses are bigger than zero, so we show the list of expenses
                mainSection = <GlobalPositionExpensesGraph immExpenses={immExpenses} />;
            } else {
                // Expenses are zero, so we show the empty state
                mainSection = (
                    <GlobalPositionExpensesEmptyState
                        message={formatText(noExpensesMessage)}
                        dark
                    />
                );
            }

            const currentMonthStyles = classnames('global-position-expenses__month-text', {
                'global-position-expenses__month-text--no-bullet': !isFilterComparing
            });

            content = (
                <div>
                    <div className="global-position-expenses__header">
                        <OKTooltip
                            content={filterButtonTooltipText}
                            offset={[0, -15]}
                            target={filterButton}
                            type="descriptive"
                        />
                        <div>
                            <h3 className="global-position-expenses__title">
                                <FormattedText value="globalPositionExpenses-thisMonthExpenses" />
                                <OKTooltip
                                    content="globalPositionExpenses-expensesByCategoryTooltip"
                                    offset={[0, -15]}
                                    target={tutorialTooltipTarget}
                                />
                            </h3>
                            <h3 className="global-position-expenses__subtitle">
                                <FormattedText value={`globalPositionExpenses-${filterType}`} />
                            </h3>
                            {currentPeriodBullet}
                            <h3 className={currentMonthStyles}>
                                {currentMonth}
                            </h3>
                            <p className="global-position-expenses__expended-amount">
                                {formattedTotalCurrentPeriod}{' '}
                                {formatCurrency(currency)}
                            </p>
                            {previousPeriodComponent}
                        </div>
                    </div>
                    <div className="row">
                        {mainSection}
                    </div>
                </div>
            );
        }

        const expensesClasses = classnames('container-fluid global-position-expenses', {
            'global-position-expenses--loading': loading
        });

        return (
            <div className={expensesClasses}>
                {content}
            </div>
        );
    }
}

GlobalPositionExpenses.propTypes = {
    children: PropTypes.any,
    customClassName: PropTypes.string,
    title: PropTypes.string,
    immExpenses: PropTypes.object,
    actions: PropTypes.object
};

module.exports = GlobalPositionExpenses;
